import { useMemo } from 'react';
import { Button, cn, Flex, Icon, Text } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

import { useRetrieveWorkspaceBookConnection } from '@/generated/hooks/bdk/v2/services/library';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { ChatActivity } from '@/shared/components/ChatActivity';

import { interpolateTemplate } from '../utils/template';
import type { TRainValueJs } from '../utils/value';
import { RainToolCallDetails } from './RainToolCallDetails';

export type TRainToolCallStatus = 'running' | 'success' | 'error';
export type TRainToolApprovalDecision = 'granted' | 'denied';

export interface IRainToolCallCardProps {
  name: string;
  statusTemplate?: string;
  status: TRainToolCallStatus;
  args: Record<string, TRainValueJs>;
  result?: TRainValueJs;
  errorMessage?: string;
  awaitingApproval?: boolean;
  approvalDecision?: TRainToolApprovalDecision;
  denialReason?: string;
  isSubmittingApproval?: boolean;
  onApprove?: () => void;
  onDeny?: () => void;
  /** Wall time the call took, in microseconds (`ToolResult.duration_micros`). */
  durationMicros?: string;
  /**
   * Shows the literal tool name beside the title and keeps the payload
   * expandable even when there is nothing in it yet.
   *
   * For surfaces whose job is to show what the thread actually did (the agent
   * builder and its test sandbox). A `statusTemplate` renders friendly copy
   * like "Reading the config" — good for an operator, useless to someone
   * debugging which tool ran, so the raw name comes back alongside it.
   */
  showToolIdentity?: boolean;
  className?: string;
}

/**
 * Microseconds as a compact duration.
 *
 * Not `formatDurationUltraCompact`, which floors anything under a second to
 * "< 1s" — most tool calls finish there, so every card would report the same
 * useless string. Sub-second calls are the common case and deserve real digits.
 */
const formatMicros = (
  durationMicros: string | undefined,
): string | undefined => {
  if (durationMicros === undefined) return undefined;
  const micros = Number(durationMicros);
  if (!Number.isFinite(micros) || micros < 0) return undefined;
  const ms = micros / 1000;
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
};

/**
 * Swap an internal `connectionId` arg for the connection's user-facing book
 * name. Returns the args unchanged when there is no `connectionId` string or
 * the lookup hasn't resolved yet, so callers can use the result as a drop-in
 * for the raw args without any fallback handling.
 */
const useResolvedConnectionArgs = (
  args: Record<string, TRainValueJs>,
): Record<string, TRainValueJs> => {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const connectionId =
    typeof args.connectionId === 'string' ? args.connectionId : '';

  const { data } = useRetrieveWorkspaceBookConnection(
    organizationId,
    workspaceId,
    connectionId,
    { query: { enabled: !!connectionId } },
  );

  const bookName = data?.data.book_connection?.book_name;

  return useMemo<Record<string, TRainValueJs>>(() => {
    if (!bookName || !connectionId) return args;
    return { ...args, connectionId: bookName };
  }, [args, bookName, connectionId]);
};

export function RainToolCallCard({
  name,
  statusTemplate,
  status,
  args,
  result,
  errorMessage,
  awaitingApproval = false,
  approvalDecision,
  denialReason,
  isSubmittingApproval = false,
  onApprove,
  onDeny,
  durationMicros,
  showToolIdentity = false,
  className,
}: IRainToolCallCardProps) {
  const { t } = useTranslation('rain');

  const displayArgs = useResolvedConnectionArgs(args);

  const isError = status === 'error';
  const isDenied = approvalDecision === 'denied';

  /*
   * A full-context surface keeps the disclosure even with nothing behind it
   * yet: arguments stream in over their own events, so a call whose payload is
   * still empty is exactly the one worth opening to watch. Curated surfaces
   * keep the old rule — no payload, no chevron — so a transcript there does not
   * fill with rows that open onto nothing.
   */
  const hasExpandableContent =
    showToolIdentity ||
    Object.keys(displayArgs).length > 0 ||
    (status !== 'running' && (result !== undefined || !!errorMessage));

  const displayName = statusTemplate
    ? interpolateTemplate(statusTemplate, displayArgs) || name
    : name;

  let title: string;
  if (awaitingApproval) {
    title = t('rain.toolCall.awaitingApproval', {
      name: displayName,
    });
  } else if (status === 'running') {
    title = t('rain.toolCall.running', { name: displayName });
  } else if (isError) {
    title = t('rain.toolCall.failed', { name: displayName });
  } else {
    title = t('rain.toolCall.completed', { name: displayName });
  }

  const duration = formatMicros(durationMicros);

  /*
   * The raw tool name and the duration, beside the title. Rendered only when
   * the name adds something the title does not already say — with no
   * `statusTemplate` the title IS the tool name, and repeating it reads as a
   * rendering bug.
   */
  const showRawName =
    showToolIdentity && !!statusTemplate && displayName !== name;
  const titleSuffix =
    showRawName || duration ? (
      <Flex align="center" gap={1.5} className="min-w-0">
        {showRawName && (
          <Text
            level="xSmall"
            className="shrink-0 font-mono text-muted-foreground"
            data-testid="rain-tool-call-name"
          >
            {name}
          </Text>
        )}
        {duration && (
          <Text
            level="xSmall"
            className="shrink-0 text-muted-foreground"
            data-testid="rain-tool-call-duration"
          >
            {duration}
          </Text>
        )}
      </Flex>
    ) : undefined;

  const showDenialLine = isDenied && !awaitingApproval;
  const denialText = denialReason
    ? t('rain.toolCall.deniedWithReason', {
        reason: denialReason,
      })
    : t('rain.toolCall.denied');

  return (
    <ChatActivity
      variant="tool-call"
      title={title}
      titleSuffix={titleSuffix}
      isActive={status === 'running' && !awaitingApproval}
      isError={isError}
      collapsible={hasExpandableContent}
      icon={
        awaitingApproval ? 'CircleHelp' : isError ? 'CircleAlert' : 'Wrench'
      }
      // The payload is a structured tree the user reads, not a dump to skim, so
      // it grows to its natural height and the transcript keeps the only
      // scrollbar. `RainValueViewer` still scrolls a too-wide value sideways.
      bodyClassName={
        showToolIdentity ? 'max-h-none overflow-y-visible' : undefined
      }
      className={className}
      data-testid="rain-tool-call-card"
      data-status={status}
      data-awaiting-approval={awaitingApproval ? 'true' : 'false'}
      data-approval-decision={approvalDecision ?? ''}
      statusContent={
        <>
          {awaitingApproval && (onApprove || onDeny) && (
            <Flex
              direction="row"
              gap={2}
              className="pl-6"
              data-testid="rain-tool-call-approval"
            >
              {/* Neither button spins: `isSubmittingApproval` does not say
                  which one was pressed, so a spinner on Approve after a
                  denial reads as though the change went through. */}
              <Button
                size="sm"
                variant="default"
                disabled={isSubmittingApproval || !onApprove}
                onClick={onApprove}
                data-testid="rain-tool-call-approve"
              >
                <Icon type="Check" size="sm" />
                {t('rain.toolCall.approve')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={isSubmittingApproval || !onDeny}
                onClick={onDeny}
                data-testid="rain-tool-call-deny"
              >
                <Icon type="X" size="sm" />
                {t('rain.toolCall.deny')}
              </Button>
            </Flex>
          )}

          {showDenialLine && (
            <span
              className="pl-6 text-xs italic text-muted-foreground"
              data-testid="rain-tool-call-denial-reason"
            >
              {denialText}
            </span>
          )}
        </>
      }
    >
      <RainToolCallDetails
        status={status}
        args={displayArgs}
        result={result}
        errorMessage={errorMessage}
        awaitingApproval={awaitingApproval}
        className={cn(!showToolIdentity && 'text-xs')}
      />
    </ChatActivity>
  );
}
